import type { IReactNativeFileUri } from "./chat";
import type { TEntityId } from "./entity-id";

export enum EKnowledgeProcessingStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
}

// NOTE: NSAI-7817 — mirrors the server's EKnowledgeProcessingErrorCode (same
// values): the stable reason a knowledge file's ingestion failed. Clients show
// the translation from KNOWLEDGE_PROCESSING_ERROR_I18N_KEYS, never the raw
// `processingError` text, which is English and written for the logs.
export enum EKnowledgeProcessingErrorCode {
  TOO_MUCH_TEXT = "too_much_text",
  NO_TEXT = "no_text",
  UNREADABLE = "unreadable",
  FILE_TOO_LARGE = "file_too_large",
  CONVERSION_TIMEOUT = "conversion_timeout",
  INTERRUPTED = "interrupted",
  TIMED_OUT = "timed_out",
  SERVICE_UNAVAILABLE = "service_unavailable",
  PROCESSING_FAILED = "processing_failed",
}

export enum EKnowledgeSource {
  PROJECT = "project",
  SYNCATOR = "syncator",
  ASSISTANT = "assistant",
  FOLDER = "folder",
}

export interface IKnowledgeFile {
  uuid: string;
  name: string;
  mimeType: string;
  fileSize: number;
  processingStatus: EKnowledgeProcessingStatus;
  processingError: string | null;
  // NOTE: NSAI-7817 — optional: a backend older than this field omits it, and
  // a file that failed before it existed has null; both fall back to the raw
  // `processingError` (or the generic failed label).
  processingErrorCode?: EKnowledgeProcessingErrorCode | null;
  chunkCount: number;
  source: EKnowledgeSource;
  isPublic: boolean;
  isOwner: boolean;
  shareCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface IKnowledgeQueryResult {
  content: string;
  score: number;
  fileName: string;
  fileUuid: string;
}

export interface IKnowledgeQueryResponse {
  results: IKnowledgeQueryResult[];
}

export interface IKnowledgeDownloadResponse {
  url: string;
  fileName: string;
  mimeType: string;
}

export interface IKnowledgeAnalytics {
  totalFiles: number;
  totalStorageBytes: number;
  totalChunks: number;
  byStatus: Record<string, number>;
  bySource: Record<string, number>;
}

export interface IKnowledgeShare {
  id: TEntityId;
  sourceProjectId: TEntityId;
  targetProjectId: TEntityId;
  targetProject: {
    id: TEntityId;
    title: string;
    uuid: string;
  } | null;
  createdAt: string;
}

export type TSharedWithMeShareType = "file_share" | "public";

export interface IKnowledgeFileShare {
  id: TEntityId;
  targetProject: {
    title: string;
    uuid: string;
  } | null;
  targetUser: {
    uuid: string;
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
  createdAt: string;
}

export interface ISharedWithMeFile {
  uuid: string;
  name: string;
  mimeType: string;
  fileSize: number;
  processingStatus: EKnowledgeProcessingStatus;
  chunkCount: number;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  shareType: TSharedWithMeShareType;
  sourceProject: {
    uuid: string;
    title: string;
  } | null;
  sharedBy: {
    firstName: string;
    lastName: string;
  } | null;
}

export interface ICreateFileShareRequest {
  fileUuid: string;
  targetProjectUuid: string;
  targetUserUuids?: string[];
}

export interface IUploadKnowledgeFileParams {
  // NOTE: React Native has no `File` — its `FormData` takes a
  // `{ uri, type, name }` descriptor instead, so the mobile app passes
  // `IReactNativeFileUri` while web passes a real `File`.
  file: File | IReactNativeFileUri;
  // NOTE: Optional. Abort the in-flight POST. axios raises a `CanceledError`
  // which `useUploadKnowledgeFile` catches to drop the row inline.
  signal?: AbortSignal;
  // NOTE: Optional. Receives 0–100 percent progress as the body uploads.
  // Backed by axios `onUploadProgress` (XHR `progress` events).
  onProgress?: (percent: number) => void;
}

// NOTE: Folder-scoped knowledge file (Claude Projects-style). The folder
// endpoints return a subset of IKnowledgeFile — no sharing/visibility, since
// folder files are private to the folder owner.
export interface IFolderKnowledgeFile {
  uuid: string;
  name: string;
  mimeType: string;
  fileSize: number;
  processingStatus: EKnowledgeProcessingStatus;
  processingError: string | null;
  // NOTE: NSAI-7817 — optional: a backend older than this field omits it, and
  // a file that failed before it existed has null; both fall back to the raw
  // `processingError` (or the generic failed label).
  processingErrorCode?: EKnowledgeProcessingErrorCode | null;
  chunkCount: number;
  source: EKnowledgeSource;
  isOwner: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IUploadFolderKnowledgeFileParams extends IUploadKnowledgeFileParams {
  folderUuid: string;
}

export interface IDeleteFolderKnowledgeFileParams {
  folderUuid: string;
  uuid: string;
}

export interface ISaveGeneratedFileToFolderParams {
  folderUuid: string;
  fileUuid: string;
}

// NOTE: NSAI-7817 — props of the knowledge upload helpers in
// utils/knowledge-upload.ts.
export interface IGetKnowledgeProcessingErrorI18nKeyProps {
  code: EKnowledgeProcessingErrorCode | null | undefined;
}

export interface IIsKnowledgeFileOverSizeLimitProps {
  fileSizeBytes: number | undefined;
  // NOTE: From /app-config; undefined on a backend that does not publish it.
  maxFileSizeMb: number | undefined;
}

export interface IClassifyKnowledgeUploadErrorProps extends IIsKnowledgeFileOverSizeLimitProps {
  // NOTE: Whatever the upload rejected with — an axios error, an abort, a
  // pre-check failure — which is why it is not narrowed further.
  error: unknown;
}

// NOTE: Literal unions so typed `t()` / `i18n.t()` accept them, the same way
// TUploadFailureI18nKey does for the shared upload copy.
export type TKnowledgeProcessingErrorI18nKey =
  | "knowledge:processingErrors.too_much_text"
  | "knowledge:processingErrors.no_text"
  | "knowledge:processingErrors.unreadable"
  | "knowledge:processingErrors.file_too_large"
  | "knowledge:processingErrors.conversion_timeout"
  | "knowledge:processingErrors.interrupted"
  | "knowledge:processingErrors.timed_out"
  | "knowledge:processingErrors.service_unavailable"
  | "knowledge:processingErrors.processing_failed";

export type TKnowledgeUploadErrorI18nKey =
  | "knowledge:error.fileTooLarge"
  | "common:upload_error.too_large"
  | "common:upload_error.network";

export interface IKnowledgeUploadErrorMessage {
  i18nKey: TKnowledgeUploadErrorI18nKey;
  i18nParams: Record<string, string | number>;
}
